/**
 * Single-source-of-truth state for Baccarat single-player + multiplayer.
 * Mirrors packages/amgbaccarat_v2/lib/presentation/bloc/game_bloc.dart.
 */
import type {
  BetType,
  GameConfig,
  GamePhase,
  GameRound,
} from '../types/game';
import { DEFAULT_GAME_CONFIG } from '../config';
import { BetCollection } from './betCollection';

export interface GameStateSnapshot {
  phase: GamePhase;
  balance: number;
  selectedChipIndex: number;
  bets: BetCollection;
  config: GameConfig;
  currentRound: GameRound | null;
  lastWinLose: number;
  history: Array<1 | 2 | 3>;
  errorMessage: string | null;
  isRiveReady: boolean;
  // multiplayer
  isMultiplayer: boolean;
  multiplayerBetSeconds: number | null;
  multiplayerRestartSeconds: number | null;
  multiplayerCanBet: boolean;
  multiplayerGameState: string | null;
  multiplayerResultString: string | null;
  multiplayerWinLose: number | null;
  playerKickedMessage: string | null;
}

type Listener = (snapshot: GameStateSnapshot, prev: GameStateSnapshot) => void;

export class GameState {
  private _phase: GamePhase = 'idle';
  private _balance = 0;
  private _selectedChipIndex = 0;
  private _bets: BetCollection = BetCollection.empty();
  private _config: GameConfig = { ...DEFAULT_GAME_CONFIG };
  private _currentRound: GameRound | null = null;
  private _lastWinLose = 0;
  private _history: Array<1 | 2 | 3> = [];
  private _errorMessage: string | null = null;
  private _isRiveReady = false;
  private _isMultiplayer = false;
  private _multiplayerBetSeconds: number | null = null;
  private _multiplayerRestartSeconds: number | null = null;
  private _multiplayerCanBet = false;
  private _multiplayerGameState: string | null = null;
  private _multiplayerResultString: string | null = null;
  private _multiplayerWinLose: number | null = null;
  private _playerKickedMessage: string | null = null;

  private listeners: Set<Listener> = new Set();

  snapshot(): GameStateSnapshot {
    return {
      phase: this._phase,
      balance: this._balance,
      selectedChipIndex: this._selectedChipIndex,
      bets: this._bets,
      config: this._config,
      currentRound: this._currentRound,
      lastWinLose: this._lastWinLose,
      history: this._history.slice(),
      errorMessage: this._errorMessage,
      isRiveReady: this._isRiveReady,
      isMultiplayer: this._isMultiplayer,
      multiplayerBetSeconds: this._multiplayerBetSeconds,
      multiplayerRestartSeconds: this._multiplayerRestartSeconds,
      multiplayerCanBet: this._multiplayerCanBet,
      multiplayerGameState: this._multiplayerGameState,
      multiplayerResultString: this._multiplayerResultString,
      multiplayerWinLose: this._multiplayerWinLose,
      playerKickedMessage: this._playerKickedMessage,
    };
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get availableBalance(): number {
    return this._balance - this._bets.total;
  }
  get phase(): GamePhase { return this._phase; }
  get bets(): BetCollection { return this._bets; }
  get config(): GameConfig { return this._config; }
  get balance(): number { return this._balance; }
  get selectedChipIndex(): number { return this._selectedChipIndex; }
  get selectedChipValue(): number {
    return this._config.chipValues[this._selectedChipIndex] ?? this._config.chipValues[0];
  }
  get lastWinLose(): number { return this._lastWinLose; }
  get currentRound(): GameRound | null { return this._currentRound; }
  get history(): ReadonlyArray<1 | 2 | 3> { return this._history; }
  get isRiveReady(): boolean { return this._isRiveReady; }
  get isMultiplayer(): boolean { return this._isMultiplayer; }
  get multiplayerCanBet(): boolean { return this._multiplayerCanBet; }
  get multiplayerBetSeconds(): number | null { return this._multiplayerBetSeconds; }
  get multiplayerRestartSeconds(): number | null { return this._multiplayerRestartSeconds; }

  setPhase(p: GamePhase): void {
    if (p === this._phase) return;
    this.commit((s) => { s._phase = p; });
  }

  setRiveReady(v: boolean): void {
    if (v === this._isRiveReady) return;
    this.commit((s) => { s._isRiveReady = v; });
  }

  setBalance(b: number): void {
    if (b === this._balance) return;
    this.commit((s) => { s._balance = b; });
  }

  setConfig(c: GameConfig): void {
    this.commit((s) => {
      s._config = { ...c };
      if (s._selectedChipIndex >= c.chipValues.length) s._selectedChipIndex = 0;
    });
  }

  selectChip(i: number): void {
    const max = this._config.chipValues.length;
    if (i < 0 || i >= max || i === this._selectedChipIndex) return;
    this.commit((s) => { s._selectedChipIndex = i; });
  }

  /**
   * Place a bet — total across all areas is checked against balance + maxBet.
   * Mirrors game_bloc.dart:122-148 (Dragon Tiger) — same rule in Baccarat,
   * see also the 2026-01-22 maxBet fix referenced in the playbook §12 item 13.
   */
  placeBet(bet: BetType, amount: number): boolean {
    if (this._phase !== 'betting') return false;
    if (amount <= 0) return false;

    const nextTotal = this._bets.total + amount;
    if (nextTotal > this._balance) return false;
    if (nextTotal > this._config.maxBet) return false;

    this.commit((s) => {
      const next = s._bets.copy();
      next.add(bet, amount);
      s._bets = next;
    });
    return true;
  }

  clearBets(): void {
    if (this._bets.isEmpty) return;
    this.commit((s) => { s._bets = BetCollection.empty(); });
  }

  /**
   * Settle a finished round: set the wallet balance to the server's
   * post-settlement value AND clear the now-resolved bets in a SINGLE
   * commit. Doing both atomically avoids a one-frame flash of an
   * intermediate `balance - bets.total` value when the displayed balance
   * is the available balance (see GameOrchestrator.applySettlement).
   */
  settle(balance: number): void {
    this.commit((s) => {
      s._balance = balance;
      s._bets = BetCollection.empty();
    });
  }

  applyRoundResult(round: GameRound): void {
    this.commit((s) => {
      s._currentRound = round;
      s._lastWinLose = round.totalWinLose;
      s._history = [
        ...s._history.slice(-9),
        s.historyValueOf(round.result),
      ] as Array<1 | 2 | 3>;
    });
  }

  resetForNewRound(): void {
    this.commit((s) => {
      s._bets = BetCollection.empty();
      s._currentRound = null;
      s._lastWinLose = 0;
      s._errorMessage = null;
    });
  }

  setError(message: string | null): void {
    if (message === this._errorMessage) return;
    this.commit((s) => {
      s._errorMessage = message;
      if (message !== null) s._phase = 'error';
    });
  }

  setMultiplayerActive(on: boolean): void {
    if (on === this._isMultiplayer) return;
    this.commit((s) => {
      s._isMultiplayer = on;
      if (!on) {
        s._multiplayerCanBet = false;
        s._multiplayerBetSeconds = null;
        s._multiplayerRestartSeconds = null;
      }
    });
  }

  setMultiplayerCanBet(on: boolean): void {
    if (on === this._multiplayerCanBet) return;
    this.commit((s) => { s._multiplayerCanBet = on; });
  }

  setMultiplayerBetSeconds(seconds: number | null): void {
    if (seconds === this._multiplayerBetSeconds) return;
    this.commit((s) => { s._multiplayerBetSeconds = seconds; });
  }

  setMultiplayerRestartSeconds(seconds: number | null): void {
    if (seconds === this._multiplayerRestartSeconds) return;
    this.commit((s) => { s._multiplayerRestartSeconds = seconds; });
  }

  setMultiplayerGameState(s: string | null): void {
    if (s === this._multiplayerGameState) return;
    this.commit((st) => { st._multiplayerGameState = s; });
  }

  setMultiplayerResultString(s: string | null): void {
    if (s === this._multiplayerResultString) return;
    this.commit((st) => { st._multiplayerResultString = s; });
  }

  setMultiplayerWinLose(n: number | null): void {
    if (n === this._multiplayerWinLose) return;
    this.commit((s) => { s._multiplayerWinLose = n; });
  }

  setPlayerKickedMessage(msg: string | null): void {
    if (msg === this._playerKickedMessage) return;
    this.commit((s) => { s._playerKickedMessage = msg; });
  }

  private historyValueOf(r: 'player' | 'banker' | 'tie'): 1 | 2 | 3 {
    return r === 'player' ? 1 : r === 'banker' ? 2 : 3;
  }

  private commit(mutator: (s: GameState) => void): void {
    const prev = this.snapshot();
    mutator(this);
    const next = this.snapshot();
    for (const l of this.listeners) {
      try {
        l(next, prev);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[GameState listener]', e);
      }
    }
  }
}
